<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <ProgressNavComponent
      v-if="signupStep > 1"
      :steps="navSteps"
      :setStep="setStep"
    />

    <!-- STEP 1: Country and the two ways in -->
    <div v-if="shownStep.step_name === StepName.CHOOSE_ONBOARDING">
      <CountryPickerComponent />

      <ChooseOnboardingComponent
        :addAuthorizationAgreementFlow="createAuthorizationFlow"
        :addNewDeviceFlow="createNewDeviceFlow"
        countryLocale="ee"
      />
    </div>

    <!-- NEW DEVICE: network contract upload, read by AI into the company and device -->
    <GridContractUploadComponent
      v-if="shownStep.step_name === StepName.CONTRACT_UPLOAD"
      :accountData="accountData"
      :hasGenerator="hasGenerator"
      :setLoading="setLoading"
      @uploadComplete="handleUploadComplete"
      class="mb-4"
    />

    <GeneratorReviewComponent
      v-if="shownStep.step_name === StepName.GENERATOR_REVIEW && hasGenerator"
      :accountData="accountData"
      @handleUpdateGenerator="signupStore.updateGenerator"
      class="mb-4"
    />

    <ContactReviewConfigurationComponent
      v-if="
        shownStep.step_name === StepName.CONTACT_REVIEW && companyIndex !== -1
      "
      :accountData="accountData"
      :companyIndex="companyIndex"
      @handleUpdateCompany="signupStore.updateCompany"
      @updateAccountData="signupStore.updateAccount"
      class="mb-4"
    />

    <ContractReviewConfigurationComponent
      v-if="
        shownStep.step_name === StepName.CONTRACT_REVIEW && companyIndex !== -1
      "
      :accountData="accountData"
      @createContainerForStartSigningReady="storeCreateContainerForStartSigning"
      class="mb-4"
    />

    <!-- Buttons: step 1 has its own choice buttons -->
    <div v-if="signupStep > 1" class="d-flex justify-content-between w-100">
      <button
        class="btn btn-secondary me-2"
        :disabled="isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>
      <button
        class="btn btn-primary"
        :disabled="isLoading"
        @click="
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? createContainerForStartSigningFunction()
            : setStep(signupStep + 1)
        "
      >
        {{
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? $t("signup.navsteps.to_signature_page")
            : $t("signup.navsteps.next")
        }}
      </button>
    </div>
    <!-- Loading overlay, shown only when isLoading is true -->
    <LoadingOverlayComponent v-if="isLoading" />
    <!-- AccountDataDebugComponent for debugging -->
    <AccountDataDebugComponent
      :accountData="accountData"
      class="mb-4"
    ></AccountDataDebugComponent>
  </div>
  <!-- Greeting modal -->
  <ModalComponent
    :submitButtonText="$t('signup.modal.hello_button')"
    @setForceToggle="setToggleHelloModal"
  >
    <template #title>{{ $t("signup.modal.hello") }}</template>
    <template #body>
      <p class="text-body-tertiary lh-lg">
        {{ $t("signup.modal.ee.section1") }}
      </p>
      <p>
        {{ $t("signup.modal.ee.section2") }}
        <a
          class="link"
          role="button"
          href="https://www.soldera.org/#faq"
          target="blank"
        >
          <font-awesome-icon icon="right-to-bracket" />
          {{ $t("signup.modal.soldera_faq") }}
        </a>
      </p>
      <div class="accordion mt-3" id="accordionExample">
        <div class="accordion-item">
          <h2 class="accordion-header" id="headingTwo">
            <button
              class="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseTwo"
              aria-expanded="false"
              aria-controls="collapseTwo"
            >
              {{ $t("signup.modal.ee.section3") }}
            </button>
          </h2>
          <div
            id="collapseTwo"
            class="accordion-collapse collapse"
            aria-labelledby="headingTwo"
            data-bs-parent="#accordionExample"
          >
            <div class="accordion-body">
              <p>
                {{ $t("signup.modal.ee.section4") }}
              </p>
              <p>
                {{ $t("signup.modal.ee.section5") }}
              </p>
            </div>
          </div>
        </div>
        <div class="accordion-item">
          <h2 class="accordion-header" id="headingOne">
            <button
              class="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#collapseOne"
              aria-expanded="false"
              aria-controls="collapseOne"
            >
              {{ $t("signup.modal.ee.section6") }}
            </button>
          </h2>
          <div
            id="collapseOne"
            class="accordion-collapse collapse"
            aria-labelledby="headingOne"
            data-bs-parent="#accordionExample"
          >
            <div class="accordion-body">
              <p>
                {{ $t("signup.modal.ee.section7") }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { useQueryManager } from "@/composables/useQueryManager";
import { StepConfig, useStepFunctions } from "@/composables/useStepFunctions";
import ChooseOnboardingComponent from "@/signup/ChooseOnboardingComponent.vue";
import { useSignupCompanySelection } from "@/signup/composables/useSignupCompanySelection";
import { useSignupStepSave } from "@/signup/composables/useSignupStepSave";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import ContactReviewConfigurationComponent from "@/signup/ee/ContactReviewConfigurationComponent.vue";
import ContractReviewConfigurationComponent from "@/signup/ee/ContractReviewConfigurationComponent.vue";
import GeneratorReviewComponent from "@/signup/ee/GeneratorReviewComponent.vue";
import GridContractUploadComponent from "@/signup/ee/GridContractUploadComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import { useSignupStore } from "@/signup/signup";
import type { GeneratorUploadResult } from "@/types";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Two ways in: authorize Soldera for Guarantees of Origin the device already receives from Elering
 * (contact details, then signing), or open a green Elering account for a device that receives none
 * yet, starting from its network contract, which the upload reads into the company and device.
 */
enum StepName {
  CHOOSE_ONBOARDING,
  CONTRACT_UPLOAD,
  GENERATOR_REVIEW,
  CONTACT_REVIEW,
  CONTRACT_REVIEW,
}

const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);

const isLoading = ref(true);
const signupStore = useSignupStore();
const {
  account: accountData,
  companyIndex,
  generatorIndex,
} = storeToRefs(signupStore);
const { getQueryValue, parsers } = useQueryManager();
const signupStep = getQueryValue("step", (value) =>
  value ? parseInt(value, 10) : 1
);
const addAuthorizationAgreement = getQueryValue(
  "addAuthorizationAgreement",
  parsers.boolean
);
const { t } = useI18n();

const { postStepCallback, takeUpload } = useSignupStepSave(isLoading);
const { choosePath } = useSignupCompanySelection("EE", isLoading);

const hasGenerator = computed(() => generatorIndex.value !== -1);

const STEP_CONFIG: Record<StepName, StepConfig> = {
  [StepName.CHOOSE_ONBOARDING]: {
    step_name: StepName.CHOOSE_ONBOARDING,
    title: t("signup.navsteps.ee.authorization.1"),
    titleKey: "signup.navsteps.ee.authorization.1",
  },
  [StepName.CONTRACT_UPLOAD]: {
    step_name: StepName.CONTRACT_UPLOAD,
    title: t("signup.navsteps.ee.new_device.2"),
    titleKey: "signup.navsteps.ee.new_device.2",
    // Validates that the upload created the device; the upload saved it itself.
    postStepCallback,
  },
  [StepName.GENERATOR_REVIEW]: {
    step_name: StepName.GENERATOR_REVIEW,
    title: t("signup.navsteps.ee.new_device.3"),
    titleKey: "signup.navsteps.ee.new_device.3",
    apiEndpoint: "/api/ee/update-generator",
    postStepCallback,
  },
  [StepName.CONTACT_REVIEW]: {
    step_name: StepName.CONTACT_REVIEW,
    title: t("signup.navsteps.ee.authorization.2"),
    titleKey: "signup.navsteps.ee.authorization.2",
    apiEndpoint: "/api/ee/update-company",
    postStepCallback,
    // The authorization path starts its company here; the upload already made the device path's.
    preStepCallback: async () => {
      signupStore.createCompanyIfMissing();
      return true;
    },
  },
  [StepName.CONTRACT_REVIEW]: {
    step_name: StepName.CONTRACT_REVIEW,
    title: t("signup.navsteps.ee.authorization.3"),
    titleKey: "signup.navsteps.ee.authorization.3",
  },
};

/** The steps both paths share, titled as the device path's own steps (its labels differ in places). */
const newDeviceTitled = (step: StepConfig, position: number): StepConfig => ({
  ...step,
  title: t(`signup.navsteps.ee.new_device.${position}`),
  titleKey: `signup.navsteps.ee.new_device.${position}`,
});

const flowSteps = computed(() =>
  addAuthorizationAgreement.value
    ? [
        STEP_CONFIG[StepName.CHOOSE_ONBOARDING],
        STEP_CONFIG[StepName.CONTACT_REVIEW],
        STEP_CONFIG[StepName.CONTRACT_REVIEW],
      ]
    : [
        newDeviceTitled(STEP_CONFIG[StepName.CHOOSE_ONBOARDING], 1),
        STEP_CONFIG[StepName.CONTRACT_UPLOAD],
        STEP_CONFIG[StepName.GENERATOR_REVIEW],
        newDeviceTitled(STEP_CONFIG[StepName.CONTACT_REVIEW], 4),
        newDeviceTitled(STEP_CONFIG[StepName.CONTRACT_REVIEW], 5),
      ]
);

const { shownStep, setStep, navSteps, invokePreStepCallback } =
  useStepFunctions(flowSteps, signupStep);

const toggleHelloModal = ref<(value: boolean) => void>();
const setToggleHelloModal = (method: (value: boolean) => void) => {
  toggleHelloModal.value = method;
};

const setLoading = (value: boolean) => {
  isLoading.value = value;
};

const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

const createAuthorizationFlow = async () => {
  toggleHelloModal.value?.(false);
  await choosePath({ addAuthorizationAgreement: "true" });
  await setStep(2);
};

const createNewDeviceFlow = async () => {
  toggleHelloModal.value?.(false);
  await choosePath({ addAuthorizationAgreement: "false" });
  await setStep(2);
};

/** The upload saved the company and device the contract describes; review the device next. */
const handleUploadComplete = async (result: GeneratorUploadResult) => {
  await takeUpload(result);
  await setStep(3);
};

onMounted(async () => {
  // A "continue registration" link opens a device's application on step 2. On the device path that
  // is the upload, which this device has already been through: go on to its review instead.
  if (
    !addAuthorizationAgreement.value &&
    shownStep.value.step_name === StepName.CONTRACT_UPLOAD &&
    hasGenerator.value
  ) {
    signupStep.value = 3;
  }
  await invokePreStepCallback();
  isLoading.value = false;
  if (accountData.value.companies.length === 0) toggleHelloModal.value?.(true);
});
</script>
